import { Link } from 'react-router-dom';
import { ArrowRight, Mail, Linkedin, Award, Users, Shield, Target, UserCircle } from 'lucide-react';
import { Button } from '../../components/ui/button';
import { Card, CardContent } from '../../components/ui/card';

export default function BoardOfTrustees() {
  const boardMembers = [
    {
      name: 'Board Chairman',
      role: 'Chairman',
      bio: 'Providing strategic leadership and oversight for Baobab Hospital\'s mission to deliver quality healthcare.',
      image: null, // Placeholder for future update
      qualifications: ['Healthcare Leadership', 'Strategic Management'],
      placeholder: true
    },
    {
      name: 'Vice Chairman',
      role: 'Vice Chairman',
      bio: 'Supporting the board\'s vision and ensuring effective governance of hospital operations.',
      image: null,
      qualifications: ['Medical Administration', 'Community Health'],
      placeholder: true
    },
    {
      name: 'Board Treasurer',
      role: 'Treasurer',
      bio: 'Overseeing financial management and ensuring responsible stewardship of hospital resources.',
      image: null,
      qualifications: ['Financial Management', 'Healthcare Finance'],
      placeholder: true
    },
    {
      name: 'Board Secretary',
      role: 'Secretary',
      bio: 'Maintaining board records and ensuring compliance with governance requirements.',
      image: null,
      qualifications: ['Administration', 'Governance'],
      placeholder: true
    },
    {
      name: 'Board Member',
      role: 'Member',
      bio: 'Contributing expertise to guide hospital policies and community health initiatives.',
      image: null,
      qualifications: ['Healthcare Policy', 'Community Development'],
      placeholder: true
    },
    {
      name: 'Board Member',
      role: 'Member',
      bio: 'Bringing valuable perspective to hospital strategic planning and operations.',
      image: null,
      qualifications: ['Medical Practice', 'Public Health'],
      placeholder: true
    },
  ];

  const governanceValues = [
    {
      icon: Shield,
      title: 'Integrity',
      description: 'Maintaining the highest ethical standards in all governance activities'
    },
    {
      icon: Target,
      title: 'Accountability',
      description: 'Taking responsibility for decisions that affect our patients and community'
    },
    {
      icon: Users,
      title: 'Transparency',
      description: 'Open communication about hospital operations and performance'
    },
    {
      icon: Award,
      title: 'Excellence',
      description: 'Striving for continuous improvement in healthcare delivery'
    }
  ];

  return (
    <div>
      {/* Hero Section */}
      <section className="bg-primary py-20 md:py-28" data-testid="board-hero">
        <div className="container-custom">
          <div className="max-w-4xl">
            <p className="text-accent font-semibold mb-3 tracking-wide uppercase text-sm">About Us</p>
            <h1 className="font-heading text-3xl md:text-4xl lg:text-5xl font-bold text-white mb-6">
              Board of Trustees
            </h1>
            <p className="text-white/80 text-lg md:text-xl leading-relaxed">
              Our Board of Trustees provides strategic guidance and oversight, ensuring that Baobab Hospital fulfills its mission of delivering quality healthcare to the Mapinga community and neighboring areas.
            </p>
          </div>
        </div>
      </section>

      {/* Board Introduction */}
      <section className="section-spacing" data-testid="board-intro">
        <div className="container-custom">
          <div className="max-w-4xl mx-auto text-center mb-16">
            <p className="text-accent font-semibold mb-2 tracking-wide uppercase text-sm">Leadership</p>
            <h2 className="font-heading text-3xl md:text-4xl font-bold text-primary mb-6">
              Guiding Our Vision
            </h2>
            <p className="text-slate-600 leading-relaxed text-lg">
              The Board of Trustees is composed of distinguished individuals committed to advancing healthcare excellence in our community. Together, they provide the strategic direction and governance oversight necessary for Baobab Hospital to achieve its mission.
            </p>
          </div>

          {/* Governance Values */}
          <div className="grid sm:grid-cols-2 lg:grid-cols-4 gap-6 mb-16">
            {governanceValues.map((value, index) => (
              <Card key={index} className="card-service text-center" data-testid={`governance-value-${index}`}>
                <CardContent className="p-6">
                  <div className="w-14 h-14 bg-accent/10 rounded-xl flex items-center justify-center mx-auto mb-4">
                    <value.icon className="w-7 h-7 text-accent" />
                  </div>
                  <h3 className="font-heading font-semibold text-primary text-lg mb-2">{value.title}</h3>
                  <p className="text-slate-600 text-sm">{value.description}</p>
                </CardContent>
              </Card>
            ))}
          </div>
        </div>
      </section>

      {/* Board Members */}
      <section className="section-spacing bg-warm" data-testid="board-members">
        <div className="container-custom">
          <div className="text-center mb-12">
            <p className="text-accent font-semibold mb-2 tracking-wide uppercase text-sm">Our Leadership</p>
            <h2 className="font-heading text-3xl md:text-4xl font-bold text-primary mb-4">
              Board Members
            </h2>
            <p className="text-slate-600 max-w-2xl mx-auto">
              Meet the dedicated individuals who guide Baobab Hospital's strategic direction.
            </p>
          </div>

          <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-8">
            {boardMembers.map((member, index) => (
              <Card key={index} className="card-doctor overflow-hidden" data-testid={`board-member-${index}`}>
                <div className="aspect-square bg-slate-100 flex items-center justify-center">
                  {member.image ? (
                    <img
                      src={member.image}
                      alt={member.name}
                      className="w-full h-full object-cover"
                    />
                  ) : (
                    <div className="text-center p-8">
                      <UserCircle className="w-24 h-24 text-slate-300 mx-auto mb-4" />
                      <p className="text-slate-400 text-sm">Profile photo coming soon</p>
                    </div>
                  )}
                </div>
                <CardContent className="p-6">
                  <span className="text-xs font-semibold text-accent uppercase tracking-wider">{member.role}</span>
                  <h3 className="font-heading font-semibold text-primary text-xl mt-1 mb-2">{member.name}</h3>
                  <p className="text-slate-600 text-sm mb-4">{member.bio}</p>
                  <div className="flex items-center gap-2 flex-wrap">
                    {member.qualifications.map((qual, i) => (
                      <span key={i} className="text-xs bg-teal-light text-primary px-3 py-1 rounded-full">
                        {qual}
                      </span>
                    ))}
                  </div>
                </CardContent>
              </Card>
            ))}
          </div>

          <div className="mt-12 text-center">
            <p className="text-slate-500 italic">
              Board member profiles will be updated with photos and detailed information as they become available.
            </p>
          </div>
        </div>
      </section>

      {/* Governance Section */}
      <section className="section-spacing" data-testid="board-governance">
        <div className="container-custom">
          <div className="max-w-4xl mx-auto">
            <div className="text-center mb-12">
              <Award className="w-16 h-16 text-accent mx-auto mb-6" />
              <h2 className="font-heading text-3xl md:text-4xl font-bold text-primary mb-6">
                Our Commitment to Governance
              </h2>
            </div>
            <div className="bg-warm rounded-2xl p-8 md:p-12">
              <p className="text-slate-600 leading-relaxed mb-6 text-lg">
                The Board of Trustees meets quarterly to review hospital operations, financial performance, and strategic initiatives. Our trustees volunteer their time and expertise to ensure that Baobab Hospital maintains the highest standards of healthcare delivery and organizational integrity.
              </p>
              <p className="text-slate-600 leading-relaxed mb-6 text-lg">
                We are committed to transparency, accountability, and continuous improvement in all aspects of our operations. The board works closely with hospital leadership to address community health needs and plan for the future of healthcare services in the Pwani region.
              </p>
              <div className="grid sm:grid-cols-3 gap-6 pt-6 border-t border-slate-200">
                <div className="text-center">
                  <p className="font-heading text-3xl font-bold text-primary">Quarterly</p>
                  <p className="text-slate-600 text-sm mt-1">Board Meetings</p>
                </div>
                <div className="text-center">
                  <p className="font-heading text-3xl font-bold text-primary">Annual</p>
                  <p className="text-slate-600 text-sm mt-1">Strategic Review</p>
                </div>
                <div className="text-center">
                  <p className="font-heading text-3xl font-bold text-primary">Continuous</p>
                  <p className="text-slate-600 text-sm mt-1">Quality Improvement</p>
                </div>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* CTA */}
      <section className="py-20 bg-primary" data-testid="board-cta">
        <div className="container-custom text-center">
          <h2 className="font-heading text-2xl md:text-3xl font-bold text-white mb-4">
            Partner With Us
          </h2>
          <p className="text-white/80 mb-8 max-w-2xl mx-auto">
            Interested in supporting Baobab Hospital's mission? Learn about partnership and collaboration opportunities.
          </p>
          <Link to="/about/collaborations" data-testid="board-collaborations">
            <Button className="bg-accent hover:bg-coral-soft text-white rounded-full px-8 py-4 font-semibold shadow-lg">
              View Collaborations
              <ArrowRight className="ml-2 w-5 h-5" />
            </Button>
          </Link>
        </div>
      </section>
    </div>
  );
}
